import { DialogActions, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { TwoPaneModalContent } from '../../../../../../ui/components/pure/modal-content/two-pane-modal-content.component';
import { TextButton } from '../../../../../../ui/components/pure/text-button/text-button.component';
import { useBookOrderActionContext } from '../book-order-action.context';
import { BookingFlowType } from '../../../../../../ui/enums/booking-flow-type.enum';
import { AllStepsLeftSide } from '../shared-components/all-steps-left-side/all-steps-left-side.component';
import { SectionTitle } from '../shared-components/section-title/section-title.component';
import { BackToSearchButton } from './back-to-search-button/back-to-search-button.component';
import { useBookOrderStepStyles } from './book-order-step.styles';
import { NewCustomerForm } from './new-customer-form/new-customer-form.component';
import { OrderDetailsForm } from './order-details-form/order-details-form.component';
import { SearchResultBox } from './search-result-box/search-result-box.component';
import { SearchResultCustomer } from './search-result-customer/search-result-customer.component';
import { NewCustomerButton } from './new-customer-button/new-customer-button.component';
import { Colors } from '../../../../../../ui/theme/colors';

export function BookOrderStep() {
  const { state, hubOptions, handlers, bookOrderStepForm, bookingFlowType } = useBookOrderActionContext();

  const classes = useBookOrderStepStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  if (state.current !== 'BOOK_ORDER') {
    return null;
  }

  return (
    <FormProvider {...bookOrderStepForm}>
      <form noValidate autoComplete="off">
        <TwoPaneModalContent
          leftDesktopWidth={4}
          rigthDesktopWidth={8}
          flexibleWidthPane="right"
          leftContent={() => <AllStepsLeftSide disableProjectSelector />}
          rightContent={() => (
            <Grid
              container
              sx={{
                justifyContent: 'flex-start',
                flexDirection: 'column'
              }}
            >
              <Grid
                container
                style={{
                  padding: '8px 16px 24px 16px',
                  borderBottom: '1px solid #EBEDF2',
                  marginLeft: 0,
                  marginRight: 0,
                  width: '100%'
                }}
                spacing={2}
                sx={{
                  justifyContent: 'flex-start',
                  flexDirection: 'column'
                }}
              >
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  spacing={2}
                  style={{ padding: '8px 0' }}
                  sx={{
                    alignItems: 'center'
                  }}
                >
                  <Grid>
                    <BackToSearchButton onClickBack={handlers.onBackToSearch} />
                  </Grid>
                  <Grid style={{ flexGrow: 1 }}>
                    <SectionTitle value="Customer" />
                    <CustomerResultCountMessage count={state.searchResults.length} />
                  </Grid>
                  {state.isCreateCustomerFormActive ? null : (
                    <Grid
                      sx={{
                        justifyContent: 'flex-end'
                      }}
                    >
                      <NewCustomerButton onClickBack={handlers.onCreateNewCustomer} />
                    </Grid>
                  )}
                </Grid>
                <ul className={classes.list}>
                  {state.searchResults.map((customer) => (
                    <li>
                      <SearchResultBox
                        alignRadioButton="center"
                        isSelected={
                          state.selectedCustomer.isExisting && state.selectedCustomer.customer.id === customer.id
                        }
                        onSelect={() => handlers.onSelectCustomer({ isExisting: true, customer })}
                      >
                        <SearchResultCustomer projectID={state.projectID} customer={customer} />
                      </SearchResultBox>
                    </li>
                  ))}
                  {state.isCreateCustomerFormActive ? (
                    <li>
                      <SearchResultBox
                        alignRadioButton="flex-start"
                        isSelected={state.selectedCustomer.isExisting === false}
                        onSelect={() => handlers.onSelectCustomer({ isExisting: false })}
                        onClose={state.searchResults.length === 0 ? undefined : handlers.onCancelCreateCustomer}
                      >
                        <NewCustomerForm />
                      </SearchResultBox>
                    </li>
                  ) : null}
                </ul>
              </Grid>
              <Grid style={{ margin: '32px 0', padding: '0 16px' }} spacing={2}>
                <OrderDetailsForm hubSelectOptions={hubOptions} />
              </Grid>
            </Grid>
          )}
        />
        <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
          <Grid
            container
            spacing={2}
            wrap="nowrap"
            sx={{
              justifyContent: 'flex-end'
            }}
          >
            {bookingFlowType === BookingFlowType.RETAIL ? (
              <>
                <Grid
                  size={{
                    xs: 6,
                    sm: 'auto'
                  }}
                >
                  <TextButton
                    color="primary"
                    // Override the color and border color to match the design
                    style={{
                      color: Colors.deliveredBlue,
                      borderColor: Colors.deliveredBlue
                    }}
                    variant="outlined"
                    fullWidth={isXs}
                    onClick={bookOrderStepForm.handleSubmit((data) => handlers.onDeliverNow(data))}
                  >
                    Deliver Now
                  </TextButton>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 'auto'
                  }}
                >
                  <TextButton
                    color="primary"
                    variant="outlined"
                    fullWidth={isXs}
                    onClick={bookOrderStepForm.handleSubmit((data) => handlers.onDeliverLater(data))}
                  >
                    Deliver Later
                  </TextButton>
                </Grid>
              </>
            ) : (
              <Grid
                size={{
                  xs: 12,
                  sm: 'auto'
                }}
              >
                <TextButton
                  color="primary"
                  variant="outlined"
                  fullWidth={isXs}
                  onClick={bookOrderStepForm.handleSubmit((data) => handlers.onDeliverLater(data))}
                >
                  Book Order
                </TextButton>
              </Grid>
            )}
          </Grid>
        </DialogActions>
      </form>
    </FormProvider>
  );
}

function CustomerResultCountMessage({ count }: { count: number }) {
  return count === 0 ? (
    <Typography color="error">No customers match the search</Typography>
  ) : (
    <Typography color="primary">
      {count} match{count > 1 ? 'es' : ''} found
    </Typography>
  );
}
